// ---------------------------------------------------------------------------
// livekitService.js — API client for /api/lk/* endpoints
// Mirrors zoomService.js in structure; zero Zoom code touched.
// ---------------------------------------------------------------------------

const apiBase = process.env.REACT_APP_LK_API_BASE || '';

async function post(path, body) {
  const res = await fetch(`${apiBase}${path}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  if (!res.ok) {
    const text = await res.text();
    throw new Error(text || `Request to ${path} failed`);
  }
  return res.json();
}

async function get(path) {
  const res = await fetch(`${apiBase}${path}`);
  if (!res.ok) {
    const text = await res.text();
    throw new Error(text || `Request to ${path} failed`);
  }
  return res.json();
}

/**
 * Teacher/host: create a LiveKit room and get a host token.
 * Returns { sessionId, token, serverUrl, roomName }
 */
export async function startLKSession({ classId, userName, userEmail }) {
  return post('/api/lk/start', { classId, userName, userEmail });
}

/**
 * Student: get a participant token for a live session.
 * Returns { sessionId, token, serverUrl, roomName }
 */
export async function joinLKSession({ classId, userName, userEmail }) {
  return post('/api/lk/join', { classId, userName, userEmail });
}

/**
 * End a live session. Called by teacher leave and via sendBeacon on tab close.
 */
export async function endLKSession(sessionId) {
  return post('/api/lk/end', { sessionId });
}

/**
 * Teacher keepalive — POST every 20s while in the room.
 */
export async function lkHeartbeat(sessionId) {
  return post('/api/lk/heartbeat', { sessionId });
}

/**
 * Poll session status for a class.
 * Returns { status: 'live'|'idle', joinable: bool, sessionId? }
 */
export async function getLKSessionStatus(classId) {
  return get(`/api/lk/status/${encodeURIComponent(classId)}`);
}

/**
 * Public — fetch meeting metadata by join token (no auth required).
 * Returns { meetingId, joinToken, type, title, scheduledAt, duration, status, participants }
 */
export async function getLKMeetingByToken(joinToken) {
  return get(`/api/lk/meeting/${encodeURIComponent(joinToken)}`);
}

/**
 * Host: start a Room Composite egress recording for the session.
 * Returns { egressId, s3Key }
 */
export async function startLKRecording(sessionId) {
  return post('/api/lk/recording/start', { sessionId });
}

/**
 * Host: stop the active recording for the session.
 * LiveKit finalises the file to S3 asynchronously.
 */
export async function stopLKRecording(sessionId) {
  return post('/api/lk/recording/stop', { sessionId });
}

/**
 * Get a 30-minute pre-signed replay URL for a session recording.
 * Returns { url, expiresIn } or throws with status 202 if still processing.
 */
export async function getLKRecordingUrl(sessionId) {
  return get(`/api/lk/recording/${encodeURIComponent(sessionId)}`);
}

/**
 * Join (or start) a session via join token.
 * Returns { sessionId, token, serverUrl, roomName, meetingId, title }
 * Throws with err.code === 'not_started' if the room hasn't been opened yet.
 */
export async function joinLKByToken({ joinToken, userId, guestName, isHost }) {
  const res = await fetch(`${apiBase}/api/lk/join-by-token`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ joinToken, userId, guestName, isHost }),
  });
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    const err = new Error(body?.error || 'Failed to join session.');
    err.code = body?.code || null;
    err.status = res.status;
    throw err;
  }
  return res.json();
}
